import { IImageRequestPayload } from "@gtmhub/files";
import { equals } from "angular";
import { Node, Group } from "konva/lib/Core";
import { Image as KonvaImage, ImageConfig } from "konva/lib/shapes/Image";
import { Transformer } from "konva/lib/shapes/Transformer";
import { ISelectionOptions } from "@gtmhub/whiteboards/rendering/behaviors/selection";
import { WhiteboardShape } from "@gtmhub/whiteboards";

export interface IPosition {
  x: number;
  y: number;
}

export interface IShapeReaction {
  userIds: string[];
  emoji: string;
  name: string;
  targetId: string;
}

export interface IShapeReactionResponse {
  reactions: IShapeReaction[];
  targetId: string;
}

export interface IShapeReactionState {
  reactions?: IShapeReaction[];
}

export const sumPositions = (...positions: IPosition[]): IPosition =>
  positions.reduce<IPosition>((sum, position) => ({ x: sum.x + position.x, y: sum.y + position.y }), zeroPosition);

export const scalePosition = (scale: IPosition, position: IPosition): IPosition => ({ x: position.x * scale.x, y: position.y * scale.y });

export const scalePositions = <N extends number, P extends { 0: IPosition; length: N } & Array<IPosition>>(scale: IPosition, positions: P): P =>
  positions.map((pos) => scalePosition(scale, pos)) as P;

export const zeroPosition: IPosition = { x: 0, y: 0 };

export const zeroSize: ISize = { width: 0, height: 0 };

export interface ISize {
  width: number;
  height: number;
}
export type BasicShapeType = "rectangle" | "ellipse" | "rhombus" | "rounded-rectangle" | "parallelogram";

export const scaleSize = (scale: IPosition, size: ISize): ISize => ({ width: size.width * scale.x, height: size.height * scale.y });

export const calculatePosition = (params: { isEllipse: boolean; position: IPosition; size: ISize }): IPosition => {
  return {
    x: params.isEllipse ? params.position.x + params.size.width / 2 : params.position.x,
    y: params.isEllipse ? params.position.y + params.size.height / 2 : params.position.y,
  };
};

/**
 * Calculates the position of a selection of nodes, which is
 * the top-most point (y) and the left-most point (x).
 */
export const selectionPosition = (...nodes: Node[]): IPosition => {
  if (!nodes.length) {
    return zeroPosition;
  }

  const firstNodePos = nodes[0].absolutePosition();
  let minX = firstNodePos.x;
  let minY = firstNodePos.y;
  for (let i = 1; i < nodes.length; i++) {
    const pos = nodes[i].absolutePosition();
    if (minX > pos.x) {
      minX = pos.x;
    }
    if (minY > pos.y) {
      minY = pos.y;
    }
  }

  return { x: minX, y: minY };
};

/**
 * Calculates the top-left and bottom-right coordinates of a selection of nodes
 * taking into consideration their position (overlapping or not).
 */
export const selectionBoundaries = (...nodes: Node[]): [IPosition, IPosition] => {
  if (!nodes.length) {
    return [zeroPosition, zeroPosition];
  }

  const imageShapeBoundaries = { size: { width: 0, height: 0 }, position: { x: 0, y: 0 } };

  const getImageIfSelected = (nodes): Node => {
    return nodes.find((x) => x.state?.type === "image" && x.attrs.selected);
  };

  const selectedImage = getImageIfSelected(nodes);

  if (selectedImage) {
    const imageShapeClientRect = selectedImage.getClientRect({ relativeTo: selectedImage.getStage() });

    imageShapeBoundaries.size = {
      width: imageShapeClientRect.width || selectedImage.width(),
      height: imageShapeClientRect.height || selectedImage.height(),
    };

    imageShapeBoundaries.position = {
      x: imageShapeClientRect.x || 0,
      y: imageShapeClientRect.y || 0,
    };
  }

  const firstNodeSize = selectedImage ? imageShapeBoundaries.size : nodeSize(nodes[0]);
  const topLeftMostPos = selectedImage ? imageShapeBoundaries.position : nodes[0].position();
  const bottomRightMostPos = {
    x: topLeftMostPos.x + firstNodeSize.width,
    y: topLeftMostPos.y + firstNodeSize.height,
  };

  for (let i = 1; i < nodes.length; i++) {
    const node = nodes[i];
    const pos = node.position();
    const { height, width } = nodeSize(node);

    topLeftMostPos.x = Math.min(topLeftMostPos.x, pos.x);
    topLeftMostPos.y = Math.min(topLeftMostPos.y, pos.y);
    bottomRightMostPos.x = Math.max(bottomRightMostPos.x, pos.x + width);
    bottomRightMostPos.y = Math.max(bottomRightMostPos.y, pos.y + height);
  }

  return [topLeftMostPos, bottomRightMostPos];
};

export const areaSize = (bottomRightPos: IPosition, topLeftPos: IPosition): ISize => {
  return {
    width: bottomRightPos.x - topLeftPos.x,
    height: bottomRightPos.y - topLeftPos.y,
  };
};

/**
 * Groups do not calculate the size properly, so we need to iterate
 * over their visible children and calculate the correct size of
 * the total group
 */
export const nodeSize = (node: Node, filter?: (node: Node) => boolean): ISize => {
  const includeNode = (node: Node): boolean => node.visible() && (!filter || filter(node));

  if (!["Group", "Layer", "Stage"].includes(node.nodeType)) {
    return includeNode(node) ? scaleSize(node.scale(), node.size()) : zeroSize;
  }

  const group = node as Group;
  let xAndWidthMax = 0;
  let yAndHeightMax = 0;

  for (const child of group.getChildren()) {
    const childSize = nodeSize(child, filter);
    if (equals(childSize, zeroSize)) {
      continue;
    }

    const childPosition = child.position();
    const xAndWidth = childPosition.x + childSize.width;
    const yAndHeight = childPosition.y + childSize.height;

    if (xAndWidthMax < xAndWidth) {
      xAndWidthMax = xAndWidth;
    }
    if (yAndHeightMax < yAndHeight) {
      yAndHeightMax = yAndHeight;
    }
  }

  return { width: xAndWidthMax, height: yAndHeightMax };
};

export interface ISizeableState {
  size: ISize;
}

export interface IFontState {
  fontSize: number;
  fontFamily: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikeThrough?: boolean;
}

export interface IHasBackgroundColor {
  backgroundColor: string;
}

export interface IHasTextColor {
  textColor: string;
}

export interface IHasHighlight {
  highlightColor: string;
}

export type TextHorizontalAlignment = "left" | "right" | "center";
export type TextVerticalAlignment = "top" | "middle" | "bottom";
export type TextAlignment = "left" | "right" | "center";

export interface Alignment {
  textHorizontalAlignment: TextHorizontalAlignment;
  textVerticalAlignment: TextVerticalAlignment;
}

export interface IHasTextAlignment {
  textAlignment: TextAlignment;
}

export interface IHasFullTextAlignment {
  textAlignment: Alignment;
}

export const addImageToGroup = (group: Group, url: string, config: Omit<ImageConfig, "image">): KonvaImage => {
  const image = new KonvaImage(config as ImageConfig);
  group.add(image);

  const imageEl = new Image();
  imageEl.onload = () => {
    // use setTimeout as IE11 is not happy if called directly
    setTimeout(() => {
      image.image(imageEl);

      // redraw if we have added the shape to a layer
      const layer = group.getLayer();
      if (layer) {
        layer.batchDraw();
      }
    });
  };

  // Gravatar does not send 'Access-Control-Allow-Origin' response header which prevents the canvas to be exported as image,
  // so we will try to load the default image
  if (url.includes("gravatar.com")) {
    const u = new URL(url);
    if (u.searchParams.has("d")) {
      url = u.searchParams.get("d");
    } else if (u.searchParams.has("default")) {
      url = u.searchParams.get("default");
    }
  }

  imageEl.crossOrigin = "Anonymous";
  imageEl.src = url;
  return image;
};

export const createImageRequestPayload = (imageUrl: string, whiteboardId: string): IImageRequestPayload => {
  return {
    targetId: whiteboardId,
    targetType: "whiteboard-shape",
    url: imageUrl,
    reference: "picture",
  };
};

export const createTransformer = (selectionOptions: ISelectionOptions): Transformer => {
  return new Transformer({
    padding: 10,
    borderStroke: "#96c6ff",
    anchorCornerRadius: 50,
    anchorSize: 7,
    anchorStroke: "#96c6ff",
    rotateAnchorOffset: 80,
    rotateEnabled: false,
    keepRatio: false,
    ignoreStroke: true,
    visible: false,
    centeredScaling: false,
    enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
    boundBoxFunc: (oldBoundBox, newBoundBox) => {
      // keep min width/height
      if (selectionOptions.minSize && (newBoundBox.width < selectionOptions.minSize || newBoundBox.height < selectionOptions.minSize)) {
        return oldBoundBox;
      }

      // keep width to height ratio for parallelogram
      if (selectionOptions.widthToHeightMinRatio && newBoundBox.width / newBoundBox.height <= selectionOptions.widthToHeightMinRatio) {
        return oldBoundBox;
      }

      return newBoundBox;
    },
  });
};

export const isBasicShape = (shape: WhiteboardShape): boolean => {
  return (
    shape.state.type === "rhombus" ||
    shape.state.type === "parallelogram" ||
    shape.state.type === "ellipse" ||
    shape.state.type === "rounded-rectangle" ||
    shape.state.type === "rectangle"
  );
};
